import type { MapDisplayPoint } from "@/features/maps/types";
import { HazardPlusTotal, ReturnPeriod, Year } from "@/features/pe_aldd";
import { screeningFloodExposureReturnPeriod } from "@/tools/risk-bands/utils/shared";
import { Rcp } from "@/types/rcp";
import { createContext, ReactNode, useContext, useState } from "react";
import type { AnalysisRouteState } from "../hooks/useAnalysisViewRoute";

/** The route-backed half comes from `useAnalysisViewRoute`; the rest is local state. */
interface CollectionState extends AnalysisRouteState {
  rcp: Rcp;
  setRcp: (rcp: Rcp) => void;
  hazard: HazardPlusTotal;
  setHazard: (hazard: HazardPlusTotal) => void;
  year: Year;
  setYear: (year: Year) => void;
  returnPeriod: ReturnPeriod;
  setReturnPeriod: (rp: ReturnPeriod) => void;
  highlightedId: string | null;
  setHighlightedId: (value: string | null) => void;
  selectedDisplayName: string | null;
  setSelectedDisplayName: (name: string | null) => void;
  selectedPoint: MapDisplayPoint | null;
  setSelectedPoint: (point: MapDisplayPoint | null) => void;
  isPointDataLoading: boolean;
  setIsPointDataLoading: (value: boolean) => void;
  riskiestPointId: string;
  setRiskiestPointId: (id: string) => void;
  // null = not chosen by the user
  clusteringEnabled: boolean | null;
  setClusteringEnabled: (enabled: boolean | null) => void;
}
const CollectionContext = createContext<CollectionState | undefined>(undefined);
export const CollectionProvider = ({
  children,
  ...route
}: AnalysisRouteState & {
  children: ReactNode;
}) => {
  const [rcp, setRcp] = useState<Rcp>("rcp85");
  const [returnPeriod, setReturnPeriod] = useState<ReturnPeriod>(screeningFloodExposureReturnPeriod);
  const [hazard, setHazard] = useState<HazardPlusTotal>("total");
  const [year, setYear] = useState<Year>(2055);
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const [selectedDisplayName, setSelectedDisplayName] = useState<string | null>(null);
  const [selectedPoint, setSelectedPoint] = useState<MapDisplayPoint | null>(null);
  const [isPointDataLoading, setIsPointDataLoading] = useState(false);
  const [riskiestPointId, setRiskiestPointId] = useState("");
  const [clusteringEnabled, setClusteringEnabled] = useState<boolean | null>(true);
  return <CollectionContext.Provider value={{
    ...route,
    rcp,
    setRcp,
    hazard,
    setHazard,
    year,
    setYear,
    returnPeriod,
    setReturnPeriod,
    highlightedId,
    setHighlightedId,
    selectedDisplayName,
    setSelectedDisplayName,
    selectedPoint,
    setSelectedPoint,
    isPointDataLoading,
    setIsPointDataLoading,
    riskiestPointId,
    setRiskiestPointId,
    clusteringEnabled,
    setClusteringEnabled
  }} data-sentry-element="unknown" data-sentry-component="CollectionProvider" data-sentry-source-file="CollectionContext.tsx">
      {children}
    </CollectionContext.Provider>;
};
export const useCollectionState = () => {
  const context = useContext(CollectionContext);
  if (context === undefined) {
    throw new Error("useCollectionState must be used within a CollectionProvider");
  }
  return context;
};